import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Project, ProjectArchetype } from '../../types/project.types';
import { Popover } from '../ui/Popover';
import { ROUTES } from '../../utils/constants';
import { useToast } from '../../hooks/useToast';
import { useUpdateProject } from '../../hooks/useProjects';
import { githubService } from '../../services/github.service';
import { ApiError } from '../../services/api';
import { LanguageSwitcher } from '../common/LanguageSwitcher';
import { AccountMenu } from '../layout/AccountMenu';

interface ProjectHeaderProps {
  project?: Project;
  hasFiles?: boolean;
}

// Minimal GitHub mark SVG (16×16 viewBox).
function GitHubIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
      <path d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.013 8.013 0 0016 8c0-4.42-3.58-8-8-8z" />
    </svg>
  );
}

function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

// Inline-editable project name.
function EditableName({ project }: { project: Project }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(project.name);
  const [shake, setShake] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const updateProject = useUpdateProject(project.id);

  // Keep local state in sync if the project name changes externally.
  useEffect(() => {
    if (!editing) setValue(project.name);
  }, [project.name, editing]);

  function startEdit() {
    setValue(project.name);
    setEditing(true);
    // Focus on next tick after re-render.
    setTimeout(() => inputRef.current?.select(), 0);
  }

  async function commit() {
    const trimmed = value.trim();
    if (!trimmed) {
      triggerShake();
      return;
    }
    if (trimmed.length > 100) {
      triggerShake();
      return;
    }
    if (trimmed === project.name) {
      setEditing(false);
      return;
    }
    try {
      await updateProject.mutateAsync({ name: trimmed });
    } catch {
      // Revert on error.
      setValue(project.name);
    }
    setEditing(false);
  }

  function cancel() {
    setValue(project.name);
    setEditing(false);
  }

  function triggerShake() {
    setShake(true);
    setTimeout(() => setShake(false), 400);
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter') {
      e.preventDefault();
      commit();
    } else if (e.key === 'Escape') {
      cancel();
    }
  }

  if (editing) {
    return (
      <input
        ref={inputRef}
        autoFocus
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={commit}
        onKeyDown={handleKeyDown}
        className={`text-base font-semibold text-gray-900 bg-transparent border-b-2 focus:outline-none w-full max-w-xs ${
          shake ? 'border-red-400 animate-[shake_0.4s_ease]' : 'border-blue-500'
        }`}
        style={shake ? { animation: 'shake 0.4s ease' } : undefined}
        maxLength={100}
      />
    );
  }

  return (
    <h1
      className="text-base font-semibold text-gray-900 cursor-pointer hover:text-blue-600 transition-colors truncate"
      onClick={startEdit}
      title="Click to rename"
    >
      {project.name}
    </h1>
  );
}

// The three deployment archetypes a project can be, in ladder order —
// shared by the badge and the override popover so neither can drift out of
// sync with the other about which archetypes exist.
const ARCHETYPES: ProjectArchetype[] = ['frontend', 'sqlite', 'fullstack'];

const ARCHETYPE_BADGE_STYLE: Record<ProjectArchetype, string> = {
  frontend: 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100',
  sqlite: 'bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100',
  fullstack: 'bg-gray-100 text-gray-600 border-gray-200 hover:bg-gray-200',
};

function archetypeLabelKey(archetype: ProjectArchetype): string {
  return `header.archetype${archetype[0].toUpperCase()}${archetype.slice(1)}`;
}

function archetypeHintKey(archetype: ProjectArchetype): string {
  return `${archetypeLabelKey(archetype)}Hint`;
}

// The hidden-but-available archetype override (increment 2 —
// docs/USE_CASES.md decisions 6/7; extended to a 3-way choice in increment 3
// for the sqlite archetype): clicking the badge reveals what the router
// chose and why, plus a one-click switch to either other archetype. Nothing
// about archetypes appears anywhere else in the flow.
function ArchetypeOverridePopover({
  project,
  open,
  onClose,
}: {
  project: Project;
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation('project');
  const { showToast } = useToast();
  const updateProject = useUpdateProject(project.id);

  async function handleSwitch(archetype: ProjectArchetype) {
    try {
      await updateProject.mutateAsync({ archetype });
      onClose();
    } catch {
      showToast('Failed to change app type.', 'error');
    }
  }

  return (
    <Popover open={open} onClose={onClose} title={t('header.archetypeOverride.title')}>
      <div className="space-y-4 text-sm">
        <div>
          <p className="text-xs text-gray-500 mb-0.5">{t('header.archetypeOverride.currentLabel')}</p>
          <p className="font-medium text-gray-900">{t(archetypeLabelKey(project.archetype))}</p>
        </div>
        <div>
          <p className="text-xs text-gray-500 mb-0.5">{t('header.archetypeOverride.rationaleLabel')}</p>
          <p className="text-gray-700">
            {project.routerRationale || t('header.archetypeOverride.rationaleFallback')}
          </p>
        </div>
        <div className="space-y-2">
          {ARCHETYPES.filter((a) => a !== project.archetype).map((a) => (
            <button
              key={a}
              type="button"
              onClick={() => handleSwitch(a)}
              disabled={updateProject.isPending}
              className="w-full text-left px-3 py-2 rounded-lg border border-gray-200 hover:border-gray-300 hover:bg-gray-50 disabled:opacity-50 transition-colors"
            >
              <p className="font-medium text-gray-900">
                {t('header.archetypeOverride.switchTo', { type: t(archetypeLabelKey(a)) })}
              </p>
              <p className="text-xs text-gray-500">{t(archetypeHintKey(a))}</p>
            </button>
          ))}
        </div>
        <p className="text-xs text-gray-400">{t('header.archetypeOverride.regenerateHint')}</p>
      </div>
    </Popover>
  );
}

// Small badge showing the project's deployment archetype next to its name.
// Clicking it opens the override popover — the badge is the only place in
// the whole flow where archetype vocabulary appears.
function ArchetypeBadge({ project }: { project: Project }) {
  const { t } = useTranslation('project');
  const [open, setOpen] = useState(false);
  const archetype = project.archetype;
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`text-[10px] font-medium uppercase tracking-wide px-2 py-0.5 rounded-full border transition-colors ${ARCHETYPE_BADGE_STYLE[archetype]}`}
        title={t(archetypeHintKey(archetype))}
      >
        {t(archetypeLabelKey(archetype))}
      </button>
      <ArchetypeOverridePopover project={project} open={open} onClose={() => setOpen(false)} />
    </>
  );
}

export function ProjectHeader({ project, hasFiles = false }: ProjectHeaderProps) {
  const { t } = useTranslation(['project', 'common']);
  const { showToast } = useToast();
  const [showPushForm, setShowPushForm] = useState(false);
  const [repoName, setRepoName] = useState('');
  const [isPrivate, setIsPrivate] = useState(true);
  const [createNew, setCreateNew] = useState(true);
  const [pushing, setPushing] = useState(false);
  const [pushSuccess, setPushSuccess] = useState(false);

  function openPushForm() {
    setRepoName(project?.name ? slugify(project.name) : '');
    setIsPrivate(true);
    setCreateNew(true);
    setShowPushForm(true);
  }

  async function handleExportGitHub() {
    try {
      const status = await githubService.getStatus();
      if (!status.connected) {
        showToast('Connect your GitHub account in Settings first.', 'info');
        return;
      }
    } catch {
      showToast('Connect your GitHub account in Settings first.', 'info');
      return;
    }
    openPushForm();
  }

  async function handlePush(e: React.FormEvent) {
    e.preventDefault();
    if (!project) return;
    setPushing(true);
    try {
      const result = await githubService.push(project.id, {
        repo_name: repoName,
        private: isPrivate,
        create_new: createNew,
      });
      setShowPushForm(false);
      setPushSuccess(true);
      showToast(`Pushed to GitHub → ${result.repo_url}`, 'success');
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Push to GitHub failed.';
      showToast(msg, 'error');
    } finally {
      setPushing(false);
    }
  }

  useEffect(() => {
    if (!pushSuccess) return;
    const t = setTimeout(() => setPushSuccess(false), 500);
    return () => clearTimeout(t);
  }, [pushSuccess]);

  return (
    <header className="bg-white border-b border-gray-200 px-4 py-2 shrink-0">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <Link
            to={ROUTES.DASHBOARD}
            className="text-gray-500 hover:text-gray-900 transition-colors shrink-0"
            aria-label={t('common:back')}
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
          </Link>
          {project ? (
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <EditableName project={project} />
              <ArchetypeBadge project={project} />
            </div>
          ) : (
            <h1 className="text-base font-semibold text-gray-900">{t('header.loading')}</h1>
          )}
        </div>
        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          <button
            onClick={handleExportGitHub}
            disabled={pushing || !hasFiles}
            className="inline-flex items-center gap-1.5 px-2 sm:px-3 py-1.5 text-sm font-medium rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            title={!hasFiles ? t('header.pushDisabledHint') : t('header.exportToGithub')}
            aria-label={t('header.exportToGithub')}
          >
            {pushing ? (
              <>
                <svg className="w-4 h-4 animate-spin shrink-0" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                </svg>
                <span className="hidden sm:inline">{t('header.pushing')}</span>
              </>
            ) : pushSuccess ? (
              <>
                <svg className="w-4 h-4 text-green-600 shrink-0" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span className="hidden sm:inline">{t('header.pushed')}</span>
              </>
            ) : (
              <>
                <GitHubIcon className="w-4 h-4 shrink-0" />
                <span className="hidden sm:inline">{t('header.exportToGithub')}</span>
              </>
            )}
          </button>
          <div className="hidden sm:block w-px h-5 bg-gray-200" aria-hidden="true" />
          <div className="hidden sm:block">
            <LanguageSwitcher />
          </div>
          <AccountMenu />
        </div>
      </div>

      <Popover open={showPushForm} onClose={() => setShowPushForm(false)} title="Push to GitHub">
        <form onSubmit={handlePush} className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Repository name
            </label>
            <input
              type="text"
              value={repoName}
              onChange={(e) => setRepoName(e.target.value)}
              required
              className="w-full text-sm border border-gray-300 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder="my-app"
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={isPrivate}
              onChange={(e) => setIsPrivate(e.target.checked)}
              className="rounded"
            />
            Private repository
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={createNew}
              onChange={(e) => setCreateNew(e.target.checked)}
              className="rounded"
            />
            Create new repository
          </label>
          <button
            type="submit"
            disabled={pushing || !repoName}
            className="w-full py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-700 disabled:opacity-50 transition-colors"
          >
            {pushing ? 'Pushing…' : 'Push to GitHub'}
          </button>
        </form>
      </Popover>
    </header>
  );
}
